/**
 * "I have read this" — §7.7's one act, for the person in the audience.
 *
 * The row it writes means *this person read this version*. Three consequences
 * shape everything here:
 *
 * - **Nobody can record it for somebody else**, and there is deliberately no
 *   route that would let them. An admin who can write the row makes the
 *   compliance view answer a different question than the one it is read for.
 * - **There is no undo.** No route removes an acknowledgment, so the button gets
 *   a confirmation rather than sitting inline — the same standard the publish
 *   dialog sets for an act that cannot be walked back.
 * - **It is scoped to a version.** Signing the current cycle says nothing about
 *   the next revision: publishing one opens a new cycle and prior
 *   acknowledgments do not carry (§7.7). The dialog says which version, by
 *   number, because "I already acknowledged that policy" is exactly the belief
 *   the version scoping exists to correct.
 *
 * A cycle that can no longer be signed — superseded, or on an archived document
 * — reads as history here rather than as a button that will 409. Which of the
 * four things this is, is {@link cycleStanding}: the panel renders one of these
 * per cycle a document has ever had, so the same component answers for the live
 * cycle and for the closed ones above it, and the branch order is the whole of
 * what it means.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useAcknowledge } from '../../api/queries.ts';
import type { AcknowledgmentCycle } from '../../api/types.ts';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { type CycleClosure, cycleClosure } from './vocabulary.tsx';

/**
 * What one cycle asks of one person, in the order the answers override.
 *
 * The precedence is the design and it is not alphabetical:
 *
 * - **Signed comes first**, and holds even after the cycle closes. The row is a
 *   record of something that happened on a day (§7.7), so a person looking at a
 *   superseded cycle they signed should be told they signed it — not that it has
 *   been superseded, which is true of the cycle and beside the point about them.
 * - **Then outside the audience.** Membership re-resolves on every read, so this
 *   is a statement about now rather than about the request.
 * - **Then closed**, because there is nothing left to press.
 * - **Only then owed**, which is the single state with a button.
 */
export type CycleStanding =
  | { kind: 'signed'; closure: CycleClosure }
  | { kind: 'not_asked' }
  | { kind: 'closed'; closure: Exclude<CycleClosure, null> }
  | { kind: 'owed' };

export function cycleStanding(cycle: AcknowledgmentCycle): CycleStanding {
  const closure = cycleClosure(cycle);
  if (cycle.viewer.acknowledged) return { kind: 'signed', closure };
  if (!cycle.viewer.inAudience) return { kind: 'not_asked' };
  if (closure) return { kind: 'closed', closure };
  return { kind: 'owed' };
}

/** What a signature still means, which depends on what became of its cycle. */
function SignedMeaning({ closure }: { closure: CycleClosure }) {
  if (closure === 'archived') {
    return (
      <>
        Recorded against that version, and it stays on the record. The document has since been
        archived, so nothing is being asked of you here now.
      </>
    );
  }
  if (closure === 'superseded') {
    return (
      <>
        Recorded against that version and no other. A revision has since been published, and it
        opened a fresh cycle that this one does not carry into — which is exactly what keeps
        &quot;everyone acknowledged the expense policy&quot; from staying true after a rewrite
        (§7.7).
      </>
    );
  }
  return (
    <>
      Recorded against that version and no other. If a revision is published, a new cycle opens and
      this does not carry over — which is what keeps &quot;everyone acknowledged the expense
      policy&quot; from staying true after a rewrite (§7.7).
    </>
  );
}

export function AcknowledgeAction({ cycle }: { cycle: AcknowledgmentCycle }) {
  const acknowledge = useAcknowledge(cycle.requestId);
  const [confirming, setConfirming] = useState(false);
  const standing = cycleStanding(cycle);

  if (standing.kind === 'signed') {
    return (
      <Alert tone="success" title={`You have acknowledged version ${cycle.versionNumber}`}>
        <SignedMeaning closure={standing.closure} />
      </Alert>
    );
  }

  if (standing.kind === 'not_asked') {
    return (
      <Alert tone="info" title="This is not asking anything of you">
        You are not in this cycle&apos;s audience. Membership is re-resolved on every read from
        roles and grants rather than from a list somebody typed, so this can change without anybody
        editing the request.
      </Alert>
    );
  }

  if (standing.kind === 'closed') {
    return standing.closure === 'archived' ? (
      <Alert tone="info" title="This document has been archived">
        It has been taken out of circulation, so there is nothing left to attest to and this cycle
        can no longer be signed (§7.7). Nothing is lost — unarchiving the document reopens it, and
        anyone who already signed stays on the record.
      </Alert>
    ) : (
      <Alert tone="info" title="This cycle has been superseded">
        A newer version has been published since it opened, so it can no longer be signed — the row
        would count for nothing and would attest to a text you are no longer reading. Acknowledge
        the current cycle instead.
      </Alert>
    );
  }

  return (
    <>
      <Alert tone="warning" title={`You owe a read of version ${cycle.versionNumber}`}>
        <div className="mt-1 flex flex-col gap-3">
          <p>
            Acknowledging records that <span className="font-semibold">you</span> read{' '}
            <span className="font-semibold">this version</span>, at the moment you press it. Nobody
            can record it on your behalf.
          </p>
          <div>
            <Button variant="primary" onPress={() => setConfirming(true)}>
              I have read this
            </Button>
          </div>
        </div>
      </Alert>

      <Dialog
        title="Acknowledge this version"
        description={`${cycle.title} — version ${cycle.versionNumber}`}
        isOpen={confirming}
        onOpenChange={setConfirming}
      >
        {(close) => (
          <>
            <DialogBody>
              <p className="text-sm text-secondary">
                This records you, by name, as having read version {cycle.versionNumber} of{' '}
                <span className="font-semibold text-primary">{cycle.title}</span> at this instant.
                There is no way to remove it afterwards.
              </p>
              <p className="text-sm text-secondary">
                It says nothing about any later revision. Publishing one opens a fresh cycle that
                you will owe again.
              </p>
              <Link
                to="/documents/$nodeId"
                params={{ nodeId: cycle.documentId }}
                className="text-sm link"
              >
                Read it once more first
              </Link>
              {acknowledge.isError ? <ErrorState error={acknowledge.error} /> : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="tertiary" onPress={close}>
                Not yet
              </Button>
              <Button
                variant="primary"
                isPending={acknowledge.isPending}
                onPress={() => acknowledge.mutate(undefined, { onSuccess: close })}
              >
                Record that I have read it
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </>
  );
}
